// import components
import {
  Footer,
  Header
} from 'components/roster-portal/home/travel-expense-claims/components';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import DutyInformationDialog from './components/DutyInformationDialog';
import DutyPeriodDialog from './components/DutyPeriodDialog';

// import external libraries
import dayjs, { type Dayjs } from 'dayjs';

// import hooks
import useDeleteDutyLine from './hooks/useDeleteDutyLine';
import useGetDutyLines from './hooks/useGetDutyLines';
import useMutateDutyLinesInBulk from './hooks/useMutateDutyLinesInBulk';
import useUpdateDutyLineFlags from './hooks/useUpdateDutyLineFlags';
import useCreateDutyLine from './hooks/useCreateDutyLine';
import useUpdateDutyLine from './hooks/useUpdateDutyLine';
import useCUDBatchDutyLines from './hooks/useCUDBatchDutyLines';
import useCreditDutyLine from './hooks/useCreditDutyLine';
import useCreateOrUpdateNominalHoursDutyLine from './hooks/useCreateOrUpdateNominalHoursDutyLine';
import { useRecalculateDutyLinesForEmployeeMutation } from 'components/admin-portal/Procedures/Realization/queries';
import { useGetCostCentersWithGivenInterval } from 'queries';
import { useGetColumns } from './hooks/useGetColumns';
import { useCopyPaste } from './hooks/useCopyPaste';
import { useRowActions } from './hooks/useRowActions';
import { useRows } from './hooks/useRows';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useRowLevelActions } from './hooks/useRowLevelActions';
import { useBulkActions } from './hooks/useBulkActions';
import { useDeleteHandler } from './hooks/useDeleteHandler';
import { usePeriodEditing } from './hooks/usePeriodEditing';
import type { DateRange } from '@mui/x-date-pickers-pro';

// import MUI constants
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';

// import styles
import {
  StyledHoursTableContainer,
  StyledHoursTableCRUDLayout
} from './HoursTable.styles';

// import types
import { DutyMutationTypeEnum } from './hooks/useMutateDutyLinesInBulk.types';
import type { DutyLineGridRow } from './HoursTable.types';
import type {
  CustomDutyLine,
  DraftDutyLine
} from 'RestAPIs/employees/hours-table/types';
import { useGridApiRef, type GridRowModesModel } from '@mui/x-data-grid-pro';

// import utils
import {
  stripForCopy,
  createEmptyDutyLine,
  resetDraft,
  isRowNominal,
  createProcessRowUpdate,
  createFlushBatch,
  buildRowClassName,
  tableTotalHours,
  computeRange,
  onRowEditStart,
  onRowEditStop,
  isCellEditable,
  isRowSelectableFromParent,
  onCellKeyDown
} from './HoursTable.utils';
import NewFeatureInformationDialog from 'components/shared/NewFeatureInformationDialog/NewFeatureInformationDialog';

const HoursTable = ({ page, employeeId }) => {
  const apiRef = useGridApiRef();
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const {
    hasEmployeePortalCreateDuty = false,
    hasEmployeePortalDutyTimeEditable = false,
    hasEmployeePortalDutyTimeEditDht = false
  } = useSelector(({ grants }) => grants);
  const { t } = useTranslation('common', {
    keyPrefix: 'hoursTable'
  });
  const { t: tRoot } = useTranslation('common');
  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);
  const { period } = useDateNavigationStore((state) => state);
  const { startDate, endDate } = computeRange(
    selectedDate,
    selectedDateRange,
    period
  );
  const [selectedRows, setSelectedRows] = useState<DutyLineGridRow[]>([]);
  const [currentRow, setCurrentRow] = useState<CustomDutyLine | null>(null);
  const [showDutyInformationDialog, setShowDutyInformationDialog] =
    useState(false);
  const [showDutyPeriodDialog, setShowDutyPeriodDialog] = useState(false);
  const [copiedRowId] = useState<string | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | number | null>(null);
  const [extraRows, setExtraRows] = useState<DraftDutyLine[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [processedRows, setProcessedRows] = useState({
    createdRows: new Map(), // keep track of all created / new rows
    updatedRows: new Map(), // keep track of all updated / existing rows
    deletedRows: new Set<number>() // keep track of all deleted rows by only adding the request id
  });
  const [editingRowId, setEditingRowId] = useState<string | number | null>(
    null
  );
  const [isRowEditModeActive, setIsRowEditModeActive] =
    useState<boolean>(false);
  const handleRowModesModelChange = useCallback(
    (newModel: GridRowModesModel) => setRowModesModel(newModel),
    []
  );
  const { data: dutyLines = [], isFetching } = useGetDutyLines(
    {
      employeeId,
      startDate,
      endDate,
      showUnpublished: isEmployeePortal ? false : true
    },
    {
      enabled: !!employeeId && !!startDate && !!endDate
    }
  );
  const { data: costCenters = [] } = useGetCostCentersWithGivenInterval(
    startDate,
    endDate,
    true,
    true
  );
  const { mutateAsync: calculateDutyLines, isError } =
    useRecalculateDutyLinesForEmployeeMutation('hoursTable');
  const { mutateAsync: mutateFlags } = useUpdateDutyLineFlags();
  const { mutateAsync: deleteDutyLine } = useDeleteDutyLine();
  const { mutateAsync: creditDutyLine } = useCreditDutyLine();
  const { mutateAsync: mutateDutyLinesInBulk } = useMutateDutyLinesInBulk();
  const { mutateAsync: createDutyLine } = useCreateDutyLine();
  const { mutateAsync: createOrUpdateNominalHoursDutyLine } =
    useCreateOrUpdateNominalHoursDutyLine();
  const { mutateAsync: updateDutyLine } = useUpdateDutyLine();
  const { mutateAsync: cudBatchDutyLines } = useCUDBatchDutyLines();

  const handleRemoveDraft = (row: DraftDutyLine) =>
    setExtraRows((prev) => prev.filter((r) => r.id !== row.id));

  const handleClearDraft = (row: DraftDutyLine) =>
    setExtraRows((prev) =>
      prev.map((r) => (r.id === row.id ? resetDraft(r) : r))
    );

  // copy-paste functionality
  const {
    copyMode,
    copySourceRowId,
    pastedRows,
    overrides,
    setCopyMode,
    setCopySourceRowId,
    setCopyPayload,
    pasteOntoRow,
    undoPaste,
    resetCopyState,
    clearRow,
    setOverrides
  } = useCopyPaste({
    apiRef,
    setProcessedRows,
    employeeId,
    setExtraRows,
    rowModesModel
  });

  // generate rows
  const rows = useRows({
    selectedDate,
    selectedDateRange,
    dutyLines,
    extraRows,
    overrides,
    t,
    tRoot,
    period
  });

  // flush batch for copy-paste
  const flushBatch = createFlushBatch({
    processedRows,
    dutyLines,
    employeeId,
    isEmployeePortal,
    cudBatchDutyLines,
    resetCopyState
  });

  // row handlers
  const {
    handleBlockToggle,
    handleCreditingToggle,
    handleNominalHoursToggle,
    handleAddRow,
    handleFinishCopy
  } = useRowLevelActions({
    apiRef,
    employeeId,
    isEmployeePortal,
    mutateFlags,
    creditDutyLine,
    flushBatch,
    setRowModesModel,
    setEditingRowId,
    setIsRowEditModeActive,
    setExtraRows,
    setCopyMode,
    createEmptyDutyLine
  });

  // bulk
  const { handleBulkActions } = useBulkActions({
    apiRef,
    selectedRows,
    setSelectedRows,
    employeeId,
    isEmployeePortal,
    mutateDutyLinesInBulk
  });

  // delete
  const { handleDelete } = useDeleteHandler({
    employeeId,
    isEmployeePortal,
    deleteDutyLine,
    mutateDutyLinesInBulk
  });

  // period editing
  const { handlePeriodCellClick, handlePeriodSave } = usePeriodEditing({
    apiRef,
    editingRowId,
    currentRow,
    setCurrentRow,
    setShowDutyPeriodDialog
  });

  const handleDutyInformationDialog = (row: CustomDutyLine) => {
    setShowDutyInformationDialog(true);
    setCurrentRow(row);
  };

  // row actions for the table
  const handleCancelRowEditAction = useCallback(
    (row: DraftDutyLine) => {
      if (!row.requestId) handleClearDraft(row);

      const resetDraft: DraftDutyLine = {
        ...row,
        isNominalRow: false,
        nominalHours: undefined
      };

      apiRef?.current?.updateRows([resetDraft]);
      setIsRowEditModeActive(false);
    },
    [apiRef, handleClearDraft]
  );

  const rowActions = useRowActions({
    t,
    tRoot,
    rowModesModel,
    copyMode,
    copySourceRowId,
    pastedRows,
    onCancelRowEditAction: handleCancelRowEditAction,
    onFinishRowEditAction: () => {
      setIsRowEditModeActive(false);
    },
    onPaste: pasteOntoRow,
    onUndo: undoPaste,
    onResetCopy: resetCopyState,
    onCopy: (row) => {
      setCopySourceRowId(row.id);
      setCopyPayload(stripForCopy(row));
      setCopyMode(true);
    },
    onAddRow: handleAddRow,
    onFinishCopy: handleFinishCopy,
    onClearDraft: handleClearDraft,
    onRemoveDraft: handleRemoveDraft,
    onBlockToggle: handleBlockToggle,
    onCreditingToggle: handleCreditingToggle,
    onDutyInformation: handleDutyInformationDialog,
    onNominalHoursToggle: handleNominalHoursToggle,
    apiRef,
    setProcessedRows,
    clearRow,
    isEmployeePortal,
    hasEmployeePortalCreateDuty,
    hasEmployeePortalDutyTimeEditable,
    hasEmployeePortalDutyTimeEditDht,
    setRowModesModel,
    setEditingRowId,
    setIsRowEditModeActive
  });

  // generate columns based on the selected date and employee
  const columns = useGetColumns({
    startDate,
    endDate,
    employeeId,
    costCenters
  });

  const processRowUpdate = createProcessRowUpdate({
    copyMode,
    employeeId,
    isEmployeePortal,
    setProcessedRows,
    setOverrides,
    createOrUpdateNominalHoursDutyLine,
    createDutyLine,
    updateDutyLine
  });

  const onSingleAction = async () => {
    if (!startDate || !endDate || isError) return;

    await calculateDutyLines({
      startDate: dayjs(startDate).toDate(),
      endDate: dayjs(endDate).toDate(),
      employeeId
    });
  };

  const handleCloseDetailDialog = () => {
    setShowDutyInformationDialog(false);
    setCurrentRow(null);
  };

  const handleCloseDutyPeriodDialog = () => {
    setShowDutyPeriodDialog(false);
  };

  const handleCancelEdit = useCallback(() => {
    if (!isRowEditModeActive || editingRowId == null || !apiRef.current) return;

    const row = apiRef.current.getRow(editingRowId);
    if (row) handleCancelRowEditAction(row);

    apiRef.current.stopRowEditMode({
      id: editingRowId,
      ignoreModifications: true
    });
    setEditingRowId(null);
  }, [isRowEditModeActive, editingRowId, apiRef, handleCancelRowEditAction]);

  const handleSelectedDateChange = useCallback(
    (date: Date | null) => {
      handleCancelEdit();
      setSelectedDate(date);
      if (period !== 'custom') {
        setSelectedDateRange(undefined);
      }
    },
    [handleCancelEdit, period, setSelectedDate, setSelectedDateRange]
  );

  const handleSelectedDateRangeChange = useCallback(
    (dateRange: DateRange<Date | Dayjs> | undefined) => {
      if (!dateRange) return;

      handleCancelEdit();

      setSelectedDateRange(dateRange);

      const [startDate] = dateRange;

      if (startDate) {
        setSelectedDate(dayjs(startDate).toDate());
      }
    },
    [handleCancelEdit, setSelectedDate, setSelectedDateRange]
  );

  useEffect(() => {
    if (period !== 'custom' && selectedDateRange) {
      setSelectedDateRange(undefined);
    }
  }, [period, selectedDateRange, setSelectedDateRange]);

  const currentRowIsDraft = currentRow != null && currentRow.requestId == null;
  const canEditCurrentRowPeriods = isEmployeePortal
    ? currentRowIsDraft
      ? hasEmployeePortalCreateDuty
      : hasEmployeePortalDutyTimeEditable
    : true;
  const canEditCurrentRowDutyHourType = isEmployeePortal
    ? hasEmployeePortalDutyTimeEditDht
    : true;
  const canChangeCurrentRowDutyCode = isEmployeePortal
    ? currentRowIsDraft && hasEmployeePortalCreateDuty
    : true;
  const canAddRemoveCurrentRowPeriods = isEmployeePortal
    ? hasEmployeePortalCreateDuty
    : true;
  const allowCopyPaste = !isEmployeePortal || hasEmployeePortalCreateDuty;

  return (
    <>
      <StyledHoursTableContainer>
        <Header
          onSelectedDate={handleSelectedDateChange}
          onSelectedDateRange={handleSelectedDateRangeChange}
          selectedDate={dayjs(selectedDate).toDate()}
          selectedDateRange={selectedDateRange}
          showSingleAction={!isRowEditModeActive && !isEmployeePortal}
          unsavedDataAlert={copyMode || isRowEditModeActive}
          unsavedDataAlertContent={
            copyMode ? t('copyModeIsActive') : t('editModeIsActive')
          }
          onSingleAction={copyMode ? handleFinishCopy : onSingleAction}
          singleActionButtonLabel={
            copyMode ? t('saveAll') : t('calculateBonuses')
          }
          showActions={!isEmployeePortal && !copyMode && !isRowEditModeActive}
          selection={copyMode ? [] : selectedRows}
          dateNavigationOptions={['week', 'month', 'fourWeeks', 'custom']}
          allowDateNavigationMenuOptions
          menuOptions={[
            {
              label: t('bulkActions.unlink'),
              onClick: () => handleBulkActions(DutyMutationTypeEnum.UNLINK)
            },
            {
              label: t('bulkActions.approve'),
              onClick: () => handleBulkActions(DutyMutationTypeEnum.ADD)
            },
            {
              label: t('bulkActions.delete'),
              onClick: () => handleBulkActions(DutyMutationTypeEnum.DELETE)
            }
          ]}
        />
        <StyledHoursTableCRUDLayout
          rowHeight={36}
          apiRef={apiRef}
          pagination={false}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          loading={isFetching}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) =>
            showErrorSnackbar(t(error.message))
          }
          onCellClick={isRowEditModeActive ? handlePeriodCellClick : undefined}
          onRowEditStart={(params, event) =>
            onRowEditStart({
              params,
              event,
              isRowEditModeActive,
              editingRowId,
              setIsRowEditModeActive,
              setEditingRowId
            })
          }
          onRowEditStop={(params, event) =>
            onRowEditStop({
              params,
              event,
              setIsRowEditModeActive,
              setEditingRowId
            })
          }
          onCellKeyDown={(params, event) =>
            onCellKeyDown({
              params,
              event,
              allowCopyPaste,
              copyMode,
              copySourceRowId,
              setCopySourceRowId,
              setCopyPayload,
              setCopyMode,
              pasteOntoRow,
              resetCopyState,
              undoPaste,
              isRowEditModeActive,
              pastedRows,
              clearRow,
              setProcessedRows,
              handleFinishCopy
            })
          }
          disableRowSelectionOnClick
          hideFooter
          editable={false}
          isCellEditable={isCellEditable({
            copyMode,
            isEmployeePortal,
            hasEmployeePortalCreateDuty,
            hasEmployeePortalDutyTimeEditable,
            hasEmployeePortalDutyTimeEditDht
          })}
          actionsThreshold={5}
          onRowClick={(params) =>
            setActiveRowId((prev) => (prev === params.id ? null : params.id))
          }
          onSelectionChange={(_, rows = []) => {
            const filteredRows = rows.filter(
              (row) =>
                (row.organizationalUnit != null && row.dvb != null) ||
                isRowNominal(row)
            );
            setSelectedRows(filteredRows);
          }}
          onDeleteClick={
            copyMode || isRowEditModeActive || isEmployeePortal
              ? undefined
              : handleDelete
          }
          canBeDeletedProp="canBeDeleted"
          checkboxSelection={!isEmployeePortal}
          isRowSelectableFromParent={(params) =>
            isRowSelectableFromParent(params)
          }
          confirmationTitle={tRoot('deleteDialog.confirmationTitle')}
          confirmationBody={tRoot('deleteDialog.confirmationBody')}
          initialState={{
            pinnedColumns: {
              left: [
                GRID_CHECKBOX_SELECTION_COL_DEF.field,
                'weekNumber',
                'date'
              ],
              right: ['actions']
            }
          }}
          getExtraRowClassName={(params) =>
            buildRowClassName(params, activeRowId, copiedRowId)
          }
          customActions={rowActions}
          footer={
            <Footer>{`${t('totalHours')}: ${tableTotalHours(rows)}`}</Footer>
          }
        />
        {showDutyInformationDialog && currentRow && (
          <DutyInformationDialog
            isOpen={showDutyInformationDialog}
            onClose={handleCloseDetailDialog}
            currentRowId={currentRow.id}
            rows={rows}
            employeeId={employeeId}
            isEmployeePortal={isEmployeePortal}
          />
        )}
        {showDutyPeriodDialog && currentRow && (
          <DutyPeriodDialog
            isOpen={showDutyPeriodDialog}
            onClose={handleCloseDutyPeriodDialog}
            row={currentRow}
            onSave={handlePeriodSave}
            employeeId={employeeId}
            canChangeDutyCode={canChangeCurrentRowDutyCode}
            canEditPeriods={canEditCurrentRowPeriods}
            canEditDutyHourType={canEditCurrentRowDutyHourType}
            canAddRemovePeriods={canAddRemoveCurrentRowPeriods}
          />
        )}
      </StyledHoursTableContainer>
      <NewFeatureInformationDialog
        title={t('newFeatureInformationDialog.title')}
        tooltipTitle={t('newFeatureInformationDialog.tooltipTitle')}
        speechBubbleText={t('newFeatureInformationDialog.speechBubbleText')}
        uniqueFeatureName={
          isEmployeePortal
            ? 'featureID - hoursTablePortal'
            : 'featureID - hoursTable'
        }
        content={
          isEmployeePortal
            ? t('newFeatureInformationDialog.contentPortal')
            : t('newFeatureInformationDialog.content')
        }
        showCoverImage={true}
        coverImageUrl={
          isEmployeePortal
            ? 'aysist/styles/external/img/hours-table-portal-info-cover.jpg'
            : 'aysist/styles/external/img/hours-table-info-cover.jpg'
        }
      />
    </>
  );
};

export default HoursTable;
